import { supabase } from '@/integrations/supabase/client';

const GAMIFICATION_TABLE = 'user_gamification_progress';
const LOCAL_STORAGE_KEY = 'lyra_gamification_progress';

interface UserGamificationProgressRow {
  user_id: string;
  completed_components: string[];
  time_spent_by_component: Record<string, number> | null;
  skills_mastered: string[];
  current_streak: number;
  longest_streak: number;
  last_active_date: string | null;
  total_time_spent: number;
  badges: any[] | null;
  level: number;
  experience: number;
  next_level_experience: number;
  created_at?: string;
  updated_at?: string;
}

export interface Badge {
  id: string;
  name: string;
  description: string;
  icon: string;
  category: 'character' | 'skill' | 'milestone' | 'special';
  characterId?: string;
  requirement: BadgeRequirement;
  unlockedAt?: Date;
  progress?: number;
}

export interface BadgeRequirement {
  type: 'component_completion' | 'time_spent' | 'streak' | 'skill_mastery' | 'first_action';
  componentIds?: string[];
  minimumTime?: number;
  streakDays?: number;
  skillId?: string;
  action?: string;
}

export interface ProgressData {
  userId: string;
  completedComponents: string[];
  timeSpentByComponent: Record<string, number>; // component ID -> minutes
  skillsMastered: string[];
  currentStreak: number;
  longestStreak: number;
  lastActiveDate: Date;
  totalTimeSpent: number; // total minutes
  badges: Badge[];
  level: number;
  experience: number;
  nextLevelExperience: number;
}

export interface ComponentProgress {
  componentId: string;
  characterId: string;
  completedAt?: Date;
  timeSpent: number; // minutes
  attempts: number;
  score?: number;
}

class GamificationService {
  private static instance: GamificationService;
  private currentUserId: string | null = null;
  private progressData: ProgressData | null = null;
  private saveTimeout: NodeJS.Timeout | null = null;
  private sessionStartTime: Date = new Date();
  private currentComponentStartTime: Map<string, Date> = new Map();

  private constructor() {
    this.loadProgress();
  }

  static getInstance(): GamificationService {
    if (!GamificationService.instance) {
      GamificationService.instance = new GamificationService();
    }
    return GamificationService.instance;
  }

  // Define all available badges
  private readonly BADGES: Badge[] = [
    // First action badges
    {
      id: 'first_ai_prompt',
      name: 'First AI Prompt',
      description: 'Complete your first AI exercise',
      icon: '🎯',
      category: 'milestone',
      requirement: { type: 'first_action', action: 'complete_exercise' }
    },
    
    // Character mastery badges
    {
      id: 'communication_pro',
      name: 'Communication Pro',
      description: 'Master all Maya\'s communication tools',
      icon: '💬',
      category: 'character',
      characterId: 'maya',
      requirement: { 
        type: 'component_completion',
        componentIds: [
          'MayaEmailComposer', 'MayaCommunicationCoach', 'MayaConfidenceBuilder',
          'MayaSubjectLineWorkshop', 'MayaTemplateLibrary', 'MayaToneChecker'
        ]
      }
    },
    {
      id: 'data_storyteller',
      name: 'Data Storyteller',
      description: 'Complete all of David\'s data visualization components',
      icon: '📊',
      category: 'character',
      characterId: 'david',
      requirement: {
        type: 'component_completion',
        componentIds: [
          'DavidDataStoryFinder', 'DavidDataVisualizer', 'DavidInsightGenerator',
          'DavidDataNarrator', 'DavidPresentationCoach', 'DavidQuickCharts'
        ]
      }
    },
    {
      id: 'automation_wizard',
      name: 'Automation Wizard',
      description: 'Finish all of Rachel\'s automation tools',
      icon: '🔮',
      category: 'character',
      characterId: 'rachel',
      requirement: {
        type: 'component_completion',
        componentIds: [
          'RachelAutomationVision', 'RachelProcessMapper', 'RachelTaskAutomator',
          'RachelWorkflowOptimizer', 'RachelEfficiencyAnalyzer', 'RachelQuickAutomation'
        ]
      }
    },
    {
      id: 'change_leader',
      name: 'Change Leader',
      description: 'Complete Alex\'s change management journey',
      icon: '🚀',
      category: 'character',
      characterId: 'alex',
      requirement: {
        type: 'component_completion',
        componentIds: [
          'AlexChangeStrategy', 'AlexStrategicPlanning', 'AlexLeadershipDevelopment',
          'AlexDecisionFramework', 'AlexOrganizationalHealth', 'AlexQuickStrategy'
        ]
      }
    },
    {
      id: 'voice_pioneer',
      name: 'Voice Pioneer',
      description: 'Master Sofia\'s voice and storytelling features',
      icon: '🎤',
      category: 'character',
      characterId: 'sofia',
      requirement: {
        type: 'component_completion',
        componentIds: [
          'SofiaVoiceDiscovery', 'SofiaStoryCreator', 'SofiaAuthenticityChecker',
          'SofiaNarrativeBuilder', 'SofiaVoiceCoach', 'SofiaVoiceRecorder'
        ]
      }
    },

    // Milestone badges
    {
      id: 'dedicated_learner',
      name: 'Dedicated Learner',
      description: 'Spend 60 minutes learning with AI',
      icon: '⏰',
      category: 'milestone',
      requirement: { type: 'time_spent', minimumTime: 60 }
    },
    {
      id: 'consistent_learner',
      name: 'Consistent Learner',
      description: 'Maintain a 7-day learning streak',
      icon: '🔥',
      category: 'milestone',
      requirement: { type: 'streak', streakDays: 7 }
    },
    {
      id: 'ai_explorer',
      name: 'AI Explorer',
      description: 'Try components from all 5 characters',
      icon: '🗺️',
      category: 'milestone',
      requirement: {
        type: 'component_completion',
        componentIds: ['maya_any', 'david_any', 'rachel_any', 'alex_any', 'sofia_any']
      }
    },
    {
      id: 'quick_learner',
      name: 'Quick Learner',
      description: 'Complete 10 components',
      icon: '⚡',
      category: 'milestone',
      requirement: { type: 'component_completion', componentIds: Array(10).fill('any') }
    },
    {
      id: 'master_learner',
      name: 'Master Learner',
      description: 'Complete 25 components',
      icon: '🎓',
      category: 'milestone',
      requirement: { type: 'component_completion', componentIds: Array(25).fill('any') }
    }
  ];

  async loadProgress(): Promise<void> {
    try {
      const { data: { user }, error } = await supabase.auth.getUser();

      if (error || !user) {
        console.warn('[gamificationService] No authenticated user found; falling back to local progress.');
        this.loadFromLocalFallback();
        return;
      }

      this.currentUserId = user.id;

      const { data, error: selectError } = await supabase
        .from(GAMIFICATION_TABLE)
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (selectError && selectError.code !== 'PGRST116') {
        throw selectError;
      }

      if (data) {
        const originalLevel = data.level || 1;
        this.progressData = this.deserializeProgress(data as UserGamificationProgressRow);

        // If level changed during deserialization (XP formula migration), persist back to Supabase
        if (this.progressData.level !== originalLevel) {
          console.log(`[gamificationService] Migration detected: Level changed from ${originalLevel} to ${this.progressData.level}. Persisting to Supabase.`);
          await this.persistToSupabase();
        }
      } else {
        this.progressData = this.createNewProgress(user.id);
        await this.persistToSupabase();
      }

      this.persistToLocal();
    } catch (error) {
      console.error('[gamificationService] Error loading progress:', error);
      this.loadFromLocalFallback();
    }
  }

  private createNewProgress(userId: string): ProgressData {
    return {
      userId,
      completedComponents: [],
      timeSpentByComponent: {},
      skillsMastered: [],
      currentStreak: 0,
      longestStreak: 0,
      lastActiveDate: new Date(),
      totalTimeSpent: 0,
      badges: [],
      level: 1,
      experience: 0,
      nextLevelExperience: this.calculateNextLevelXP(1) // 50 XP needed for level 2
    };
  }

  async saveProgress(): Promise<void> {
    if (!this.progressData) return;

    // Save to localStorage immediately (synchronous, won't be lost on navigation)
    this.persistToLocal();

    // Clear any pending Supabase save
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
    }

    // Debounce Supabase saves to avoid excessive API calls
    this.saveTimeout = setTimeout(async () => {
      try {
        await this.persistToSupabase();
      } catch (error) {
        console.error('[gamificationService] Error saving progress to Supabase:', error);
      }
    }, 1000);
  }

  startComponent(componentId: string): void {
    this.currentComponentStartTime.set(componentId, new Date());
    this.updateStreak();
  }

  completeComponent(componentId: string, score?: number): ComponentProgress {
    if (!this.progressData) {
      this.progressData = this.createNewProgress('anonymous');
    }

    const startTime = this.currentComponentStartTime.get(componentId);
    const timeSpent = startTime 
      ? Math.round((new Date().getTime() - startTime.getTime()) / 60000) 
      : 0;

    // Update progress data
    if (!this.progressData.completedComponents.includes(componentId)) {
      this.progressData.completedComponents.push(componentId);
      this.addExperience(50); // Base XP for completion
    }

    // Update time tracking
    this.progressData.timeSpentByComponent[componentId] = 
      (this.progressData.timeSpentByComponent[componentId] || 0) + timeSpent;
    this.progressData.totalTimeSpent += timeSpent;

    // Check for new badges
    this.checkBadges();

    // Save progress
    this.saveProgress();

    // Clean up
    this.currentComponentStartTime.delete(componentId);

    return {
      componentId,
      characterId: this.getCharacterFromComponent(componentId),
      completedAt: new Date(),
      timeSpent,
      attempts: 1,
      score
    };
  }

  private getCharacterFromComponent(componentId: string): string {
    if (componentId.toLowerCase().includes('maya')) return 'maya';
    if (componentId.toLowerCase().includes('david')) return 'david';
    if (componentId.toLowerCase().includes('rachel')) return 'rachel';
    if (componentId.toLowerCase().includes('alex')) return 'alex';
    if (componentId.toLowerCase().includes('sofia')) return 'sofia';
    return 'unknown';
  }

  private updateStreak(): void {
    if (!this.progressData) return;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const lastActive = new Date(this.progressData.lastActiveDate);
    lastActive.setHours(0, 0, 0, 0);

    const daysDiff = Math.floor((today.getTime() - lastActive.getTime()) / (1000 * 60 * 60 * 24));

    if (daysDiff === 0) {
      // Same day, no change
    } else if (daysDiff === 1) {
      // Next day, increment streak
      this.progressData.currentStreak++;
      this.progressData.longestStreak = Math.max(
        this.progressData.currentStreak, 
        this.progressData.longestStreak
      );
    } else {
      // Streak broken
      this.progressData.currentStreak = 1;
    }

    this.progressData.lastActiveDate = new Date();
  }

  private checkBadges(): void {
    if (!this.progressData) return;

    this.BADGES.forEach(badge => {
      // Skip if already unlocked
      if (this.progressData!.badges.some(b => b.id === badge.id)) return;

      let unlocked = false;
      const progress = this.calculateBadgeProgress(badge);

      switch (badge.requirement.type) {
        case 'component_completion':
          unlocked = progress >= 1;
          break;
        case 'time_spent':
          unlocked = this.progressData.totalTimeSpent >= (badge.requirement.minimumTime || 0);
          break;
        case 'streak':
          unlocked = this.progressData.currentStreak >= (badge.requirement.streakDays || 0);
          break;
        case 'first_action':
          unlocked = this.progressData.completedComponents.length > 0;
          break;
      }

      if (unlocked) {
        const unlockedBadge = { ...badge, unlockedAt: new Date(), progress: 1 };
        this.progressData.badges.push(unlockedBadge);
        this.addExperience(100); // XP for badge unlock
        this.onBadgeUnlocked(unlockedBadge);
      }
    });
  }

  private calculateBadgeProgress(badge: Badge): number {
    if (!this.progressData) return 0;

    switch (badge.requirement.type) {
      case 'component_completion':
        if (!badge.requirement.componentIds) return 0;
        
        // Handle special cases
        if (badge.requirement.componentIds.includes('any')) {
          return Math.min(1, this.progressData.completedComponents.length / badge.requirement.componentIds.length);
        }
        
        // Check character-specific components
        if (badge.requirement.componentIds.some(id => id.includes('_any'))) {
          const characterCounts: Record<string, number> = {};
          this.progressData.completedComponents.forEach(comp => {
            const char = this.getCharacterFromComponent(comp);
            characterCounts[char] = (characterCounts[char] || 0) + 1;
          });
          const requiredChars = badge.requirement.componentIds.filter(id => id.includes('_any'));
          const hasAllChars = requiredChars.every(req => {
            const char = req.replace('_any', '');
            return characterCounts[char] > 0;
          });
          return hasAllChars ? 1 : requiredChars.filter(req => {
            const char = req.replace('_any', '');
            return characterCounts[char] > 0;
          }).length / requiredChars.length;
        }
        
        // Regular component completion
        const completed = badge.requirement.componentIds.filter(id => 
          this.progressData!.completedComponents.includes(id)
        ).length;
        return completed / badge.requirement.componentIds.length;

      case 'time_spent':
        return Math.min(1, this.progressData.totalTimeSpent / (badge.requirement.minimumTime || 1));

      case 'streak':
        return Math.min(1, this.progressData.currentStreak / (badge.requirement.streakDays || 1));

      case 'first_action':
        return this.progressData.completedComponents.length > 0 ? 1 : 0;

      default:
        return 0;
    }
  }

  private addExperience(amount: number): void {
    if (!this.progressData) return;

    const oldLevel = this.progressData.level;

    // Add to cumulative XP (never resets)
    this.progressData.experience += amount;

    // Recalculate level based on cumulative XP
    this.progressData.level = this.calculateLevelFromXP(this.progressData.experience);

    // Update next level threshold for display
    this.progressData.nextLevelExperience = this.calculateNextLevelXP(this.progressData.level);

    // Trigger level-up events for each level gained
    if (this.progressData.level > oldLevel) {
      for (let lvl = oldLevel + 1; lvl <= this.progressData.level; lvl++) {
        this.onLevelUp(lvl);
      }
    }
  }

  // CUMULATIVE XP THRESHOLDS - Total XP needed to reach each level
  // Experience is cumulative (never resets), levels are milestones
  private readonly LEVEL_THRESHOLDS: Record<number, number> = {
    1: 0,      // Start
    2: 50,     // 50 total XP (1 lesson)
    3: 150,    // 150 total XP (3 lessons cumulative)
    4: 300,    // 300 total XP (6 lessons cumulative)
    5: 500,    // 500 total XP (10 lessons cumulative)
    6: 750,    // 750 total XP
    7: 1050,   // 1050 total XP
    8: 1400,   // 1400 total XP
    9: 1800,   // 1800 total XP
    10: 2300,  // 2300 total XP
    11: 2900,  // 2900 total XP
    12: 3600,  // 3600 total XP
    13: 4400,  // 4400 total XP
    14: 5300,  // 5300 total XP
    15: 6300,  // 6300 total XP
    16: 7500,  // 7500 total XP
    17: 8900,  // 8900 total XP
    18: 10500, // 10500 total XP
    19: 12500, // 12500 total XP
    20: 15000  // 15000 total XP
  };

  // Get the cumulative XP threshold needed to reach a specific level
  private getLevelThreshold(level: number): number {
    if (level <= 1) return 0;
    if (level <= 20) return this.LEVEL_THRESHOLDS[level] || 0;
    // Beyond level 20: exponential growth
    return Math.floor(15000 * Math.pow(1.2, level - 20));
  }

  // Get cumulative XP needed for the NEXT level (used for display)
  private calculateNextLevelXP(level: number): number {
    return this.getLevelThreshold(level + 1);
  }

  // Calculate what level a user should be based on their cumulative XP
  private calculateLevelFromXP(totalXP: number): number {
    let level = 1;
    while (level < 100 && totalXP >= this.getLevelThreshold(level + 1)) {
      level++;
    }
    return level;
  }

  // Get level title based on current level - each level has a unique title
  getLevelTitle(level: number): string {
    const titles: Record<number, string> = {
      1: 'Newcomer',
      2: 'Curious Learner',
      3: 'Active Explorer',
      4: 'Prompt Crafter',
      5: 'AI Whisperer',
      6: 'Context Master',
      7: 'Pattern Spotter',
      8: 'Iteration Pro',
      9: 'Output Refiner',
      10: 'Strategy Sage',
      11: 'Workflow Wizard',
      12: 'Efficiency Expert',
      13: 'AI Architect',
      14: 'Prompt Virtuoso',
      15: 'AI Champion',
      16: 'System Thinker',
      17: 'Innovation Lead',
      18: 'AI Strategist',
      19: 'Master Collaborator',
      20: 'AI Mentor'
    };
    return titles[level] || 'AI Legend';
  }

  // Get level color for UI styling
  getLevelColor(level: number): string {
    if (level >= 20) return 'from-amber-400 to-yellow-500'; // Gold
    if (level >= 15) return 'from-purple-500 to-pink-500'; // Purple
    if (level >= 10) return 'from-blue-500 to-cyan-400';   // Blue
    if (level >= 7) return 'from-green-500 to-emerald-400'; // Green
    if (level >= 5) return 'from-teal-400 to-cyan-400';    // Teal
    if (level >= 3) return 'from-indigo-400 to-blue-400';  // Indigo
    return 'from-slate-400 to-gray-400';                   // Gray
  }

  // Get all level progression info for display (shows levels 1-20)
  // Returns cumulative XP thresholds for each level
  getLevelProgression(): Array<{ level: number; title: string; xpRequired: number; cumulativeXp: number }> {
    const levels = [];

    for (let level = 1; level <= 20; level++) {
      // xpRequired = cumulative XP needed to reach this level
      const cumulativeXp = this.getLevelThreshold(level);
      // For display, show the threshold for the NEXT level (what you need to reach)
      const nextThreshold = this.getLevelThreshold(level + 1);
      levels.push({
        level,
        title: this.getLevelTitle(level),
        xpRequired: nextThreshold, // Cumulative XP needed to reach next level
        cumulativeXp // Cumulative XP to reach this level
      });
    }

    return levels;
  }

  // Get the cumulative XP threshold for reaching a specific level (public accessor)
  getLevelThresholdPublic(level: number): number {
    return this.getLevelThreshold(level);
  }

  // Get XP required for a specific level (public accessor)
  getXPForLevel(level: number): number {
    return this.calculateNextLevelXP(level);
  }

  private onBadgeUnlocked(badge: Badge): void {
    // Trigger notification/celebration
    if (typeof window !== 'undefined' && window.dispatchEvent) {
      window.dispatchEvent(new CustomEvent('badge-unlocked', { detail: badge }));
    }
  }

  private onLevelUp(level: number): void {
    // Trigger notification/celebration
    if (typeof window !== 'undefined' && window.dispatchEvent) {
      window.dispatchEvent(new CustomEvent('level-up', { detail: { level } }));
    }
  }

  private loadFromLocalFallback(): void {
    try {
      const savedProgress = typeof window !== 'undefined' ? localStorage.getItem(LOCAL_STORAGE_KEY) : null;
      if (savedProgress) {
        const parsed = JSON.parse(savedProgress);

        // Experience is cumulative (total XP ever earned)
        let experience = parsed.experience || 0;
        const storedLevel = parsed.level || 1;

        // MIGRATION: If old system stored per-level XP, convert to cumulative
        const expectedMinXP = this.getLevelThreshold(storedLevel);
        if (experience < expectedMinXP && storedLevel > 1) {
          console.log(`[gamificationService] Local migration: Converting to cumulative XP. Old: level ${storedLevel} with ${experience} XP`);
          experience = expectedMinXP + experience;
          console.log(`[gamificationService] Local migration: New cumulative XP: ${experience}`);
        }

        // Calculate level from cumulative XP (authoritative source)
        const level = this.calculateLevelFromXP(experience);
        const nextLevelExperience = this.calculateNextLevelXP(level);

        if (level !== storedLevel) {
          console.log(`[gamificationService] Local level recalculated: was ${storedLevel}, now ${level} based on ${experience} cumulative XP`);
        }

        // If streak is 0 but user has completed components, they should have at least 1 day streak
        let currentStreak = parsed.currentStreak || 0;
        if (currentStreak === 0 && (parsed.completedComponents?.length || 0) > 0) {
          currentStreak = 1;
        }

        this.progressData = {
          ...parsed,
          lastActiveDate: parsed.lastActiveDate ? new Date(parsed.lastActiveDate) : new Date(),
          badges: this.normalizeBadges(parsed.badges || []),
          level,
          experience,
          nextLevelExperience,
          currentStreak,
          longestStreak: Math.max(parsed.longestStreak || 0, currentStreak)
        };
        if (parsed.userId && !this.currentUserId) {
          this.currentUserId = parsed.userId;
        }
      } else {
        this.progressData = this.createNewProgress(this.currentUserId || 'anonymous');
      }
    } catch (error) {
      console.error('[gamificationService] Error loading local progress:', error);
      this.progressData = this.createNewProgress(this.currentUserId || 'anonymous');
    }
  }

  private persistToLocal(): void {
    if (!this.progressData || typeof window === 'undefined') return;
    try {
      const serialized = {
        ...this.progressData,
        lastActiveDate: this.progressData.lastActiveDate.toISOString(),
        badges: this.serializeBadges(this.progressData.badges)
      };
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(serialized));
    } catch (error) {
      console.error('[gamificationService] Error saving local progress:', error);
    }
  }

  private deserializeProgress(row: UserGamificationProgressRow): ProgressData {
    // Experience is cumulative (total XP ever earned)
    let experience = row.experience || 0;

    // MIGRATION: If old system stored per-level XP, we need to convert to cumulative
    // Old system: level 2 with 0 XP meant they just leveled up
    // New system: level 2 means they have at least 50 cumulative XP
    const storedLevel = row.level || 1;
    const expectedMinXP = this.getLevelThreshold(storedLevel);

    // If stored XP is less than what's needed for their level, they were on old system
    // Add the threshold XP to convert to cumulative
    if (experience < expectedMinXP && storedLevel > 1) {
      console.log(`[gamificationService] Migration: Converting from per-level XP to cumulative. Old: level ${storedLevel} with ${experience} XP`);
      experience = expectedMinXP + experience; // Their progress toward next level is added to threshold
      console.log(`[gamificationService] Migration: New cumulative XP: ${experience}`);
    }

    // Calculate level from cumulative XP (authoritative source of truth)
    const level = this.calculateLevelFromXP(experience);
    const nextLevelExperience = this.calculateNextLevelXP(level);

    if (level !== storedLevel) {
      console.log(`[gamificationService] Level recalculated: was ${storedLevel}, now ${level} based on ${experience} cumulative XP`);
    }

    // If streak is 0 but user has completed components, they should have at least 1 day streak
    let currentStreak = row.current_streak || 0;
    if (currentStreak === 0 && (row.completed_components?.length || 0) > 0) {
      currentStreak = 1; // At least 1 day of activity
    }

    return {
      userId: row.user_id,
      completedComponents: row.completed_components || [],
      timeSpentByComponent: row.time_spent_by_component || {},
      skillsMastered: row.skills_mastered || [],
      currentStreak,
      longestStreak: Math.max(row.longest_streak || 0, currentStreak),
      lastActiveDate: row.last_active_date ? new Date(row.last_active_date) : new Date(),
      totalTimeSpent: row.total_time_spent || 0,
      badges: this.normalizeBadges(row.badges || []),
      level,
      experience,
      nextLevelExperience
    };
  }

  private normalizeBadges(rawBadges: any[]): Badge[] {
    return (rawBadges || []).map((badge: any) => ({
      ...badge,
      unlockedAt: badge.unlockedAt ? new Date(badge.unlockedAt) : undefined
    }));
  }

  private serializeBadges(badges: Badge[]): any[] {
    return (badges || []).map((badge) => ({
      ...badge,
      unlockedAt: badge.unlockedAt ? badge.unlockedAt.toISOString() : null
    }));
  }

  private async persistToSupabase(): Promise<void> {
    if (!this.progressData || !this.currentUserId) {
      return;
    }

    const payload: UserGamificationProgressRow = {
      user_id: this.currentUserId,
      completed_components: this.progressData.completedComponents,
      time_spent_by_component: this.progressData.timeSpentByComponent,
      skills_mastered: this.progressData.skillsMastered,
      current_streak: this.progressData.currentStreak,
      longest_streak: this.progressData.longestStreak,
      last_active_date: this.progressData.lastActiveDate.toISOString().slice(0, 10),
      total_time_spent: this.progressData.totalTimeSpent,
      badges: this.serializeBadges(this.progressData.badges),
      level: this.progressData.level,
      experience: this.progressData.experience,
      next_level_experience: this.progressData.nextLevelExperience
    };

    const { error } = await supabase.from(GAMIFICATION_TABLE).upsert(payload);
    if (error) {
      throw error;
    }
  }

  getProgress(): ProgressData | null {
    return this.progressData;
  }

  getBadges(): Badge[] {
    return this.BADGES;
  }

  getUnlockedBadges(): Badge[] {
    return this.progressData?.badges || [];
  }

  getComponentProgress(componentId: string): ComponentProgress | null {
    if (!this.progressData) return null;

    const completed = this.progressData.completedComponents.includes(componentId);
    const timeSpent = this.progressData.timeSpentByComponent[componentId] || 0;

    if (!completed && timeSpent === 0) return null;

    return {
      componentId,
      characterId: this.getCharacterFromComponent(componentId),
      completedAt: completed ? new Date() : undefined,
      timeSpent,
      attempts: completed ? 1 : 0
    };
  }

  getSuggestedNextComponent(): string | null {
    if (!this.progressData) return null;

    const allComponents = [
      'MayaEmailComposer', 'MayaCommunicationCoach', 'MayaConfidenceBuilder',
      'DavidDataStoryFinder', 'DavidDataVisualizer', 'DavidInsightGenerator',
      'RachelAutomationVision', 'RachelProcessMapper', 'RachelTaskAutomator',
      'AlexChangeStrategy', 'AlexStrategicPlanning', 'AlexLeadershipDevelopment',
      'SofiaVoiceDiscovery', 'SofiaStoryCreator', 'SofiaAuthenticityChecker'
    ];

    const incompleteComponents = allComponents.filter(
      comp => !this.progressData!.completedComponents.includes(comp)
    );

    return incompleteComponents.length > 0 ? incompleteComponents[0] : null;
  }

  resetProgress(): void {
    const targetUserId = this.currentUserId || 'anonymous';
    this.progressData = this.createNewProgress(targetUserId);
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
      this.saveTimeout = null;
    }
    this.persistToLocal();
    void this.persistToSupabase().catch((error) => {
      console.error('[gamificationService] Failed to reset progress in Supabase:', error);
    });
  }
}

export const gamificationService = GamificationService.getInstance();
